import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import type { Note } from '@/utils/types';
import { getNote } from '@/api/notes';
import EditForm from '@/components/v1/Notes/EditForm';

export default function EditNote() {
  const { id } = useParams<{ id: string }>();
  const [note, setNote] = useState<Note | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const noteId = id != null ? parseInt(id, 10) : NaN;

  useEffect(() => {
    if (Number.isNaN(noteId)) {
      setError('Invalid note ID');
      setLoading(false);
      return;
    }
    let cancelled = false;
    async function fetchNote() {
      try {
        setLoading(true);
        setError(null);
        const data = await getNote(noteId);
        if (!cancelled) setNote(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load note');
          setNote(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchNote();
    return () => {
      cancelled = true;
    };
  }, [noteId]);

  if (Number.isNaN(noteId)) {
    return (
      <div className="bg-gray-900 min-h-screen w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-red-400">Invalid note ID.</p>
          <Link to="/backend" className="mt-4 inline-block text-indigo-400 hover:text-indigo-300">
            Back to Backend
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-gray-900 min-h-screen w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-gray-400">Loading…</p>
        </div>
      </div>
    );
  }

  if (error || !note) {
    return (
      <div className="bg-gray-900 min-h-screen w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-red-400" role="alert">
            {error ?? 'Note not found'}
          </p>
          <Link to="/backend" className="mt-4 inline-block text-indigo-400 hover:text-indigo-300">
            Back to Backend
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 min-h-screen w-full">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white">
            Edit Note
          </h1>
          <EditForm note={note} />
        </motion.div>
      </div>
    </div>
  );
}
